import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiError } from '../utils/apiError';
import { format } from 'date-fns';
import { Mail, Send, Loader2, Plus, Save, AlertCircle } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import SafeHtml from './SafeHtml';
import RichTextEditor from './RichTextEditor';
import EmailChipInput from './EmailChipInput';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ConversationView = ({ leadId, leadEmail }) => {
    const [emails, setEmails] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [replyMode, setReplyMode] = useState(false);
    const [composeMode, setComposeMode] = useState(false);
    const [showReplyCcBcc, setShowReplyCcBcc] = useState(false);
    const [showComposeCcBcc, setShowComposeCcBcc] = useState(false);
    const [replyData, setReplyData] = useState({
        account_id: '',
        to_emails: [],
        cc_emails: [],
        bcc_emails: [],
        subject: '',
        body: '',
        in_reply_to: null,
        references: null,
        reply_all: false
    });
    const [composeData, setComposeData] = useState({
        account_id: '',
        to_emails: [],
        cc_emails: [],
        bcc_emails: [],
        subject: '',
        body: ''
    });
    const [sending, setSending] = useState(false);
    const [missingFields, setMissingFields] = useState([]);
    const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
    const [templateName, setTemplateName] = useState('');

    useEffect(() => {
        fetchConversation();
        fetchAccounts();
    }, [leadId]);

    const fetchConversation = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/emails/thread/${leadId}`);
            setEmails(response.data);
        } catch (error) {
            console.error('Failed to fetch conversation:', error);
            toast.error('Failed to load conversation');
        } finally {
            setLoading(false);
        }
    };

    const fetchAccounts = async () => {
        try {
            const response = await axios.get(`${API_URL}/accounts`);
            setAccounts(response.data);
        } catch (error) {
            console.error('Failed to fetch accounts:', error);
        }
    };

    const startCompose = () => {
        const defaultAccount = accounts[0];
        const bodyWithSignature = defaultAccount?.signature_html ? `<br/><br/>${defaultAccount.signature_html}` : '';

        setComposeData({
            account_id: defaultAccount?.id || '',
            to_emails: leadEmail ? [leadEmail] : [],
            cc_emails: [],
            bcc_emails: [],
            subject: '',
            body: bodyWithSignature
        });

        setComposeMode(true);
        setReplyMode(false);
    };

    const startReply = (email, replyAll = false) => {
        const subject = email.subject.startsWith('Re: ')
            ? email.subject
            : `Re: ${email.subject}`;

        // Get my email addresses
        const myEmails = accounts.map(acc => acc.email.toLowerCase());

        // Prepare To field
        let toEmails = [email.from_email];

        // Prepare CC field for Reply All
        let ccEmails = [];
        if (replyAll) {
            // Add original To recipients (excluding self)
            const originalTo = email.to_email ? [email.to_email] : [];
            ccEmails = [...originalTo, ...(email.cc_emails || [])]
                .filter(e => e && !myEmails.includes(e.toLowerCase()))
                .filter((e, i, arr) => arr.indexOf(e) === i); // unique
        }

        // Use the account that received this email, or default to first account
        const replyAccount = accounts.find(acc => acc.id === email.account_id) || accounts[0];
        const bodyWithSignature = replyAccount?.signature_html ? `<br/><br/>${replyAccount.signature_html}` : '';

        setReplyData({
            account_id: replyAccount?.id || '',
            to_emails: toEmails,
            cc_emails: ccEmails,
            bcc_emails: [],
            subject: subject,
            body: bodyWithSignature,
            in_reply_to: email.message_id,
            // Build references chain: append current message_id to existing references
            references: email.references
                ? `${email.references} ${email.message_id}`
                : email.message_id || null,
            reply_all: replyAll
        });

        setShowReplyCcBcc(replyAll && ccEmails.length > 0);
        setReplyMode(true);
        setComposeMode(false);
    };

    const handleAccountSelect = (accountId, isCompose = false) => {
        const account = accounts.find(acc => acc.id === accountId);
        if (account) {
            if (isCompose) {
                // Get the previous account ID and find its signature
                const previousAccountId = composeData.account_id;
                const previousAccount = previousAccountId ? accounts.find(a => a.id === previousAccountId) : null;
                const previousSignature = previousAccount?.signature_html || '';

                // Remove the previous account's signature from the body
                const bodyWithoutSignature = previousSignature
                    ? composeData.body.replace(new RegExp(`<br/><br/>${previousSignature.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`), '').trim()
                    : composeData.body.trim();

                // Append the new account's signature
                const bodyWithSignature = account.signature_html
                    ? (bodyWithoutSignature ? `${bodyWithoutSignature}<br/><br/>${account.signature_html}` : `<br/><br/>${account.signature_html}`)
                    : bodyWithoutSignature;
                setComposeData({ ...composeData, account_id: accountId, body: bodyWithSignature });
            } else {
                // Get the previous account ID and find its signature
                const previousAccountId = replyData.account_id;
                const previousAccount = previousAccountId ? accounts.find(a => a.id === previousAccountId) : null;
                const previousSignature = previousAccount?.signature_html || '';

                // Remove the previous account's signature from the body
                const bodyWithoutSignature = previousSignature
                    ? replyData.body.replace(new RegExp(`<br/><br/>${previousSignature.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`), '').trim()
                    : replyData.body.trim();

                // Append the new account's signature
                const bodyWithSignature = account.signature_html
                    ? (bodyWithoutSignature ? `${bodyWithoutSignature}<br/><br/>${account.signature_html}` : `<br/><br/>${account.signature_html}`)
                    : bodyWithoutSignature;
                setReplyData({ ...replyData, account_id: accountId, body: bodyWithSignature });
            }
        } else {
            if (isCompose) {
                setComposeData({ ...composeData, account_id: accountId });
            } else {
                setReplyData({ ...replyData, account_id: accountId });
            }
        }
    };

    const validateVariables = async (body, subject) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_URL}/emails/validate-variables`,
                {
                    body,
                    subject,
                    lead_id: leadId
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (!response.data.valid) {
                const missingFields = response.data.missing_fields || [];
                setMissingFields(missingFields); // Update state for UI sync
                return { isValid: false, missingFields };
            }
            setMissingFields([]); // Update state for UI sync
            return { isValid: true, missingFields: [] };
        } catch (error) {
            console.error('Failed to validate variables:', error);
            setMissingFields([]); // Update state for UI sync
            return { isValid: true, missingFields: [] }; // Allow sending if validation fails
        }
    };

    const sendEmail = async (isCompose = false) => {
        const data = isCompose ? composeData : replyData;

        if (!data.account_id || data.to_emails.length === 0 || !data.subject || !data.body) {
            toast.error('Please fill all required fields');
            return;
        }

        // Validate variables before sending
        const { isValid, missingFields: validationMissingFields } = await validateVariables(data.body, data.subject);
        if (!isValid) {
            toast.error(`Missing required fields: ${validationMissingFields.join(', ')}. Please add fallbacks or fill missing data.`);
            return;
        }

        try {
            setSending(true);
            // Send all To emails as actual To recipients
            const payload = {
                account_id: data.account_id,
                to_email: data.to_emails[0], // Primary recipient
                subject: data.subject,
                body: data.body,
                cc_emails: data.cc_emails,
                bcc_emails: data.bcc_emails,
                lead_id: leadId
            };

            // If multiple To recipients, add additional ones to CC (with deduplication)
            if (data.to_emails.length > 1) {
                // Create a Set of existing email addresses (case-insensitive)
                const existingEmails = new Set(
                    [
                        payload.to_email,
                        ...(payload.cc_emails || [])
                    ].map(email => email?.toLowerCase()).filter(Boolean)
                );

                // Filter out duplicates from additional To recipients
                const additionalToRecipients = data.to_emails.slice(1)
                    .filter(email => email && !existingEmails.has(email.toLowerCase()));

                // Merge unique additional recipients into CC
                if (additionalToRecipients.length > 0) {
                    payload.cc_emails = [...(payload.cc_emails || []), ...additionalToRecipients];
                    // Notify user that extra To recipients were moved to CC
                    toast.info(`Only one primary recipient is supported. ${additionalToRecipients.length} additional recipient${additionalToRecipients.length > 1 ? 's were' : ' was'} placed in CC.`);
                }
            }

            // Add reply-specific fields only if replying
            if (!isCompose) {
                payload.in_reply_to = replyData.in_reply_to;
                payload.references = replyData.references;
            }

            const response = await axios.post(`${API_URL}/emails/send`, payload);
            toast.success(isCompose ? 'Email sent successfully' : 'Reply sent successfully');

            // Show notification if tasks were auto-completed (Story 7.3)
            if (response.data?.tasks_completed > 0) {
                toast.success(`Task 'Email ${data.to_emails[0]}' marked as complete.`);
            }

            if (isCompose) {
                setComposeMode(false);
                setShowComposeCcBcc(false);
                setComposeData({
                    account_id: '',
                    to_emails: [],
                    cc_emails: [],
                    bcc_emails: [],
                    subject: '',
                    body: ''
                });
            } else {
                setReplyMode(false);
                setShowReplyCcBcc(false);
                setReplyData({
                    account_id: '',
                    to_emails: [],
                    cc_emails: [],
                    bcc_emails: [],
                    subject: '',
                    body: '',
                    in_reply_to: null,
                    references: null,
                    reply_all: false
                });
            }

            fetchConversation();
        } catch (error) {
            if (error.response?.status === 400 && error.response?.data?.missing_fields) {
                const missingFields = error.response.data.missing_fields;
                setMissingFields(missingFields);
                toast.error(`Missing required fields: ${missingFields.join(', ')}. Please add fallbacks or fill missing data.`);
            } else {
                toast.error(isCompose ? 'Failed to send email' : 'Failed to send reply');
            }
        } finally {
            setSending(false);
        }
    };

    const sendReply = async () => {
        await sendEmail(false);
    };

    const sendCompose = async () => {
        await sendEmail(true);
    };

    const cancelReply = () => {
        setReplyMode(false);
        setShowReplyCcBcc(false);
        setReplyData({
            account_id: '',
            to_emails: [],
            cc_emails: [],
            bcc_emails: [],
            subject: '',
            body: '',
            in_reply_to: null,
            references: null,
            reply_all: false
        });
    };

    const cancelCompose = () => {
        setComposeMode(false);
        setShowComposeCcBcc(false);
        setMissingFields([]);
        setComposeData({
            account_id: '',
            to_emails: [],
            cc_emails: [],
            bcc_emails: [],
            subject: '',
            body: ''
        });
    };

    const saveAsTemplate = async () => {
        if (!templateName.trim()) {
            toast.error('Please enter a template name');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_URL}/templates`,
                {
                    name: templateName,
                    subject: composeData.subject,
                    body_html: composeData.body,
                    is_global: true
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            toast.success('Template saved successfully');
            setSaveTemplateDialogOpen(false);
            setTemplateName('');
        } catch (error) {
            toast.error(getApiError(error, 'Failed to save template'));
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200 flex-shrink-0">
                <h3 className="text-lg font-semibold text-slate-900">Conversation History</h3>
                <div className="flex items-center gap-3">
                    {emails.length > 0 && (
                        <span className="text-sm text-slate-500">{emails.length} messages</span>
                    )}
                    {!composeMode && !replyMode && (
                        <Button
                            size="sm"
                            onClick={startCompose}
                            className="h-8 text-sm"
                        >
                            <Plus className="h-3 w-3 mr-2" />
                            Compose
                        </Button>
                    )}
                </div>
            </div>

            {emails.length === 0 && !composeMode && (
                <div className="flex-1 flex flex-col items-center justify-center text-center py-8 text-slate-500">
                    <Mail className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="mb-4">No conversation history yet</p>
                    <Button
                        size="sm"
                        onClick={startCompose}
                        className="h-8 text-sm"
                    >
                        <Plus className="h-3 w-3 mr-2" />
                        Send First Email
                    </Button>
                </div>
            )}

            {emails.length > 0 && (
                <ScrollArea className="flex-1 min-h-0 pr-4">
                    <div className="space-y-4 pb-4">
                        {emails.map((email) => (
                            <div
                                key={email.id}
                                className={`flex ${email.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                            >
                                <div
                                    className={`max-w-[80%] rounded-lg p-4 ${email.direction === 'outbound'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-white border border-slate-200 text-slate-900'
                                        }`}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex-1">
                                            <p className={`text-xs font-medium ${email.direction === 'outbound' ? 'text-blue-100' : 'text-slate-500'
                                                }`}>
                                                {email.direction === 'outbound' ? `You (${email.from_email})` : email.from_email}
                                            </p>
                                            <p className={`text-xs ${email.direction === 'outbound' ? 'text-blue-200' : 'text-slate-400'
                                                }`}>
                                                {format(new Date(email.created_at), 'PPp')}
                                            </p>
                                        </div>
                                    </div>
                                    {email.subject && (
                                        <p className={`font-medium text-sm mb-2 ${email.direction === 'outbound' ? 'text-white' : 'text-slate-900'
                                            }`}>
                                            {email.subject}
                                        </p>
                                    )}
                                    <SafeHtml
                                        content={email.body}
                                        className={`text-sm ${email.direction === 'outbound' ? 'text-white' : 'text-slate-700'
                                            }`}
                                    />
                                    {email.direction === 'inbound' && !replyMode && !composeMode && (
                                        <div className="flex gap-2 mt-3">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 text-xs"
                                                onClick={() => startReply(email, false)}
                                            >
                                                <Send className="h-3 w-3 mr-1" />
                                                Reply
                                            </Button>
                                            {(email.cc_emails && email.cc_emails.length > 0) || (email.to_email && email.to_email !== leadEmail) ? (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-7 text-xs"
                                                    onClick={() => startReply(email, true)}
                                                >
                                                    <Send className="h-3 w-3 mr-1" />
                                                    Reply All
                                                </Button>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </ScrollArea>
            )}

            {composeMode && (
                <div className="mt-4 pt-4 border-t border-slate-200 flex-shrink-0 bg-white">
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium text-slate-700 mb-1 block">From Account</label>
                            <Select
                                value={composeData.account_id}
                                onValueChange={(value) => handleAccountSelect(value, true)}
                            >
                                <SelectTrigger className="h-8">
                                    <SelectValue placeholder="Select account" />
                                </SelectTrigger>
                                <SelectContent>
                                    {accounts.map((acc) => (
                                        <SelectItem key={acc.id} value={acc.id}>{acc.email}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-700 mb-1 block">To</label>
                            <EmailChipInput
                                emails={composeData.to_emails}
                                onChange={(emails) => setComposeData({ ...composeData, to_emails: emails })}
                                placeholder="recipient@example.com"
                            />
                        </div>
                        {!showComposeCcBcc && composeData.cc_emails.length === 0 && composeData.bcc_emails.length === 0 && (
                            <button
                                onClick={() => setShowComposeCcBcc(true)}
                                type="button"
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                                + Show Cc/Bcc
                            </button>
                        )}
                        {(showComposeCcBcc || composeData.cc_emails.length > 0 || composeData.bcc_emails.length > 0) && (
                            <>
                                <div>
                                    <label className="text-xs font-medium text-slate-700 mb-1 block">Cc</label>
                                    <EmailChipInput
                                        emails={composeData.cc_emails}
                                        onChange={(emails) => setComposeData({ ...composeData, cc_emails: emails })}
                                        placeholder="cc@example.com"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-700 mb-1 block">Bcc</label>
                                    <EmailChipInput
                                        emails={composeData.bcc_emails}
                                        onChange={(emails) => setComposeData({ ...composeData, bcc_emails: emails })}
                                        placeholder="bcc@example.com"
                                    />
                                </div>
                            </>
                        )}
                        <div>
                            <label className="text-xs font-medium text-slate-700 mb-1 block">Subject</label>
                            <input
                                type="text"
                                value={composeData.subject}
                                onChange={(e) => {
                                    setComposeData({ ...composeData, subject: e.target.value });
                                    if (missingFields.length > 0) {
                                        validateVariables(composeData.body, e.target.value);
                                    }
                                }}
                                className={`w-full h-8 px-3 text-sm border rounded-lg focus:outline-none focus:ring-2 ${missingFields.length > 0 ? 'border-red-300 focus:ring-red-500' : 'border-slate-200 focus:ring-blue-500'
                                    }`}
                                placeholder="Email subject"
                            />
                        </div>
                        {missingFields.length > 0 && (
                            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-red-900">Missing Required Fields</p>
                                        <p className="text-xs text-red-700 mt-1">
                                            The following variables are missing data: {missingFields.map(f => `{{${f}}}`).join(', ')}
                                        </p>
                                        <p className="text-xs text-red-600 mt-1">
                                            Add fallbacks like {`{{${missingFields[0]} | there}}`} or fill the missing lead data.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-medium text-slate-700 mb-1 block">Message</label>
                            <RichTextEditor
                                content={composeData.body}
                                accountId={composeData.account_id}
                                leadId={leadId}
                                onChange={(html) => {
                                    setComposeData({ ...composeData, body: html });
                                    if (missingFields.length > 0) {
                                        validateVariables(html, composeData.subject);
                                    }
                                }}
                                placeholder="Type your message... Type / to insert a template"
                                onMissingFields={setMissingFields}
                                onSubjectChange={(subject) => setComposeData({ ...composeData, subject })}
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button
                                onClick={sendCompose}
                                disabled={sending || missingFields.length > 0}
                                className="flex-1 h-8 text-sm"
                            >
                                {sending ? (
                                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                                ) : (
                                    <Send className="h-3 w-3 mr-2" />
                                )}
                                Send Email
                            </Button>
                            <Dialog open={saveTemplateDialogOpen} onOpenChange={setSaveTemplateDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="h-8 text-sm"
                                        onClick={() => setSaveTemplateDialogOpen(true)}
                                    >
                                        <Save className="h-3 w-3 mr-2" />
                                        Save as Template
                                    </Button>
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-md rounded-2xl">
                                    <DialogHeader>
                                        <DialogTitle className="text-2xl font-semibold text-slate-900">Save as Template</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 mt-4">
                                        <div>
                                            <Label>Template Name</Label>
                                            <Input
                                                value={templateName}
                                                onChange={(e) => setTemplateName(e.target.value)}
                                                placeholder="e.g., Follow-up V2"
                                                className="rounded-lg"
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={saveAsTemplate}
                                                className="flex-1 rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                            >
                                                <Save className="h-4 w-4 mr-2" />
                                                Save Template
                                            </Button>
                                            <Button
                                                onClick={() => setSaveTemplateDialogOpen(false)}
                                                variant="outline"
                                                className="rounded-lg font-medium transition-all active:scale-95"
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    </div>
                                </DialogContent>
                            </Dialog>
                            <Button
                                onClick={cancelCompose}
                                variant="outline"
                                className="h-8 text-sm"
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {replyMode && (
                <div className="mt-4 pt-4 border-t border-slate-200 flex-shrink-0 bg-white">
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium text-slate-700 mb-1 block">From Account</label>
                            <Select
                                value={replyData.account_id}
                                onValueChange={handleAccountSelect}
                            >
                                <SelectTrigger className="h-8">
                                    <SelectValue placeholder="Select account" />
                                </SelectTrigger>
                                <SelectContent>
                                    {accounts.map((acc) => (
                                        <SelectItem key={acc.id} value={acc.id}>{acc.email}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-700 mb-1 block">To</label>
                            <EmailChipInput
                                emails={replyData.to_emails}
                                onChange={(emails) => setReplyData({ ...replyData, to_emails: emails })}
                                placeholder="recipient@example.com"
                            />
                        </div>
                        {!showReplyCcBcc && replyData.cc_emails.length === 0 && (
                            <button
                                onClick={() => setShowReplyCcBcc(true)}
                                type="button"
                                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                                + Show Cc/Bcc
                            </button>
                        )}
                        {(showReplyCcBcc || replyData.cc_emails.length > 0) && (
                            <>
                                <div>
                                    <label className="text-xs font-medium text-slate-700 mb-1 block">Cc</label>
                                    <EmailChipInput
                                        emails={replyData.cc_emails}
                                        onChange={(emails) => setReplyData({ ...replyData, cc_emails: emails })}
                                        placeholder="cc@example.com"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-700 mb-1 block">Bcc</label>
                                    <EmailChipInput
                                        emails={replyData.bcc_emails}
                                        onChange={(emails) => setReplyData({ ...replyData, bcc_emails: emails })}
                                        placeholder="bcc@example.com"
                                    />
                                </div>
                            </>
                        )}
                        <div>
                            <label className="text-xs font-medium text-slate-700 mb-1 block">Subject</label>
                            <input
                                type="text"
                                value={replyData.subject}
                                onChange={(e) => setReplyData({ ...replyData, subject: e.target.value })}
                                className="w-full h-8 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-700 mb-1 block">Message</label>
                            <RichTextEditor
                                content={replyData.body}
                                accountId={replyData.account_id}
                                leadId={leadId}
                                onChange={(html) => setReplyData({ ...replyData, body: html })}
                                placeholder="Type your reply..."
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button
                                onClick={sendReply}
                                disabled={sending}
                                className="flex-1 h-8 text-sm"
                            >
                                {sending ? (
                                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                                ) : (
                                    <Send className="h-3 w-3 mr-2" />
                                )}
                                Send Reply
                            </Button>
                            <Button
                                onClick={cancelReply}
                                variant="outline"
                                className="h-8 text-sm"
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConversationView;
